/**
 * Runtime `role` strings from the API (e.g. GET /me). Use these for `===` / `includes`.
 * OpenAPI-generated *RoleEnum display values (e.g. "Admin (0)") are not the wire format.
 */
export const Role = {
  Admin: "Admin",
  Editor: "Editor",
  Commenter: "Commenter",
  Viewer: "Viewer",
  SuperAdmin: "SuperAdmin",
  Guest: "Guest",
} as const;

export type AppRole = (typeof Role)[keyof typeof Role];

export function hasRole(
  userRole: string | undefined,
  ...allowed: string[]
): boolean {
  return !!userRole && allowed.includes(userRole);
}

export function isSuperAdmin(role: string | undefined): boolean {
  return role === Role.SuperAdmin;
}

export function isGuestRole(role: string | undefined): boolean {
  return role === Role.Guest;
}

/** GET/POST /api/Project, org — JSDoc: Admin, SuperAdmin */
export const ROLES_PROJECT_ORG_ADMIN = [Role.Admin, Role.SuperAdmin] as const;

/** Kullanıcı listesi / ekleme / silme (UserApi) — Admin ve SuperAdmin */
export function canManageUserAccounts(role: string | undefined): boolean {
  return hasRole(role, ...ROLES_PROJECT_ORG_ADMIN);
}

/**
 * Başka kullanıcı hesaplarını yönetme (liste, silme vb.) — Admin, SuperAdmin.
 * Kendi profilini güncelleme için `canUpdateOwnUserProfile` kullanın.
 */
export function canEditUserProfile(role: string | undefined): boolean {
  return canManageUserAccounts(role);
}

/** Ayarlarda kendi ad, unvan, avatar, şifre vb. — oturum açmış tüm roller (Guest dahil). */
export function canUpdateOwnUserProfile(role: string | undefined): boolean {
  return !!role;
}

/** POST entry/file/folder mutations — JSDoc: Admin, SuperAdmin, Editor */
export const ROLES_CONTENT_EDITOR = [Role.Admin, Role.SuperAdmin, Role.Editor] as const;

/** POST /api/Entry/change-status — JSDoc: Admin, SuperAdmin, Editor, Commenter */
export const ROLES_STATUS_CHANGE = [
  Role.Admin,
  Role.SuperAdmin,
  Role.Editor,
  Role.Commenter,
] as const;

export function canChangeEntryStatus(role: string | undefined): boolean {
  return hasRole(role, ...ROLES_STATUS_CHANGE);
}

/** Editor/Commenter: statü değişimi dosya detayı veya versiyon ekranında */
export function canChangeEntryStatusOnPage(
  role: string | undefined,
  context?: { pathname?: string; hasFileDetail?: boolean }
): boolean {
  if (!canChangeEntryStatus(role)) return false;
  if (hasRole(role, Role.Admin, Role.SuperAdmin)) return true;
  const path =
    context?.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '');
  const onFileContext = path.includes('/versions') || !!context?.hasFileDetail;
  return hasRole(role, Role.Editor, Role.Commenter) && onFileContext;
}

/** Entry move + duplicate (EntryApi) — Admin, SuperAdmin, Editor */
export function canMoveOrDuplicateEntry(role: string | undefined): boolean {
  return hasRole(role, ...ROLES_CONTENT_EDITOR);
}

/** File comment create/resolve/pin — JSDoc: Admin, SuperAdmin, Editor, Commenter */
export const ROLES_COMMENT_INTERACTION = [Role.Admin, Role.SuperAdmin, Role.Editor, Role.Commenter] as const;

/** GET /api/User/emails (UserApi) — JSDoc: Admin, SuperAdmin, Editor, Commenter (aynı rol seti) */
export function canSearchUserEmails(role: string | undefined): boolean {
  return hasRole(role, ...ROLES_COMMENT_INTERACTION);
}

/** GET /api/File/{id}/activity-logs — JSDoc: Admin, SuperAdmin */
export const ROLES_ACTIVITY_LOGS = [Role.Admin, Role.SuperAdmin] as const;

/**
 * Internal menü / dashboard bölümü: Guest dışındaki tüm roller için (Viewer dahil) her zaman göster.
 * Guest: yalnızca external; Internal satırı yok.
 */
export function canSeeInternalProjectsNav(role: string | undefined): boolean {
  if (!role) return false;
  if (role === Role.Guest) return false;
  return true;
}

export function isReadOnlyClient(role: string | undefined): boolean {
  return role === Role.Viewer;
}

/** Deprecated wire names (pre–role-refresh) → canonical role for display/filters */
export const LEGACY_ROLE_TO_WIRE: Record<string, string> = {
  AccountManager: Role.Admin,
  AccountOwner: Role.Admin,
  InternalTeam: Role.Editor,
  BrandAccountViewer: Role.Viewer,
  AgencyAccountViewer: Role.Viewer,
};

/**
 * Kullanıcı oluşturma formu OpenAPI etiketleriyle ("Viewer (3)"); POST /api/User body `role: "Viewer"`.
 * Zaten kısa isimse (eski sürüm) aynen geçer.
 */
const CREATE_USER_FORM_LABEL_TO_WIRE: Record<string, string> = {
  "Admin (0)": Role.Admin,
  "Editor (1)": Role.Editor,
  "Commenter (2)": Role.Commenter,
  "Viewer (3)": Role.Viewer,
  "Süper Yönetici (4)": Role.SuperAdmin,
  "Guest (5)": Role.Guest,
};

export function mapCreateUserFormRoleToWire(formRole: string | undefined): string | undefined {
  if (formRole == null || formRole === "") return undefined;
  return CREATE_USER_FORM_LABEL_TO_WIRE[formRole] ?? formRole;
}